import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { DashboardLayout } from "../dashboard/DashboardLayout";
import { createBanner } from "../../redux/actions/bannerActions";
import { toast } from "react-toastify";
import { FaArrowLeft, FaImage, FaUpload } from "react-icons/fa";
import { FiX, FiCalendar } from "react-icons/fi";
import { FullScreenLoader } from "../Components/Loader";
import dayjs from "dayjs";

const CreateBanner = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);
  const { creating } = useSelector((state) => state.banner || {});

  const [formData, setFormData] = useState({
    banner_type: "",
    image: null,
    imagePreview: null,
    is_live: true,
    role_ids: [],
    start_date: "",
    end_date: "",
  });

  const [errors, setErrors] = useState({});
  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setErrors((prev) => ({
        ...prev,
        image: "Please select a valid image file",
      }));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({
        ...prev,
        image: "Image size should be less than 5MB",
      }));
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData((prev) => ({
        ...prev,
        image: reader.result, // Base64 string
        imagePreview: reader.result,
      }));
      setErrors((prev) => ({ ...prev, image: "" }));
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setFormData((prev) => ({
      ...prev,
      image: null,
      imagePreview: null,
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRoleChange = (roleId) => {
    setFormData((prev) => {
      const roleIds = prev.role_ids || [];
      const newRoleIds = roleIds.includes(roleId)
        ? roleIds.filter((id) => id !== roleId)
        : [...roleIds, roleId];
      return { ...prev, role_ids: newRoleIds };
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.banner_type.trim()) {
      newErrors.banner_type = "Banner type is required";
    }
    if (!formData.image) {
      newErrors.image = "Image is required";
    }
    if (formData.start_date && formData.end_date) {
      const start = dayjs(formData.start_date);
      const end = dayjs(formData.end_date);
      if (start.isAfter(end)) {
        newErrors.end_date = "End date must be after start date";
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    try {
      const bannerData = {
        banner_type: formData.banner_type,
        image: formData.image,
        is_live: formData.is_live,
        role_ids: formData.role_ids,
      };

      // Add dates if provided
      if (formData.start_date) {
        bannerData.start_date = dayjs(formData.start_date).toISOString();
      }
      if (formData.end_date) {
        bannerData.end_date = dayjs(formData.end_date).toISOString();
      }

      await dispatch(createBanner(bannerData, userInfo.access_token));
      // toast.success("Banner created successfully!");
      navigate("/dashboard/bannerlist");
    } catch (error) {
      console.error("Error creating banner:", error);
    }
  };

  // Available roles (you can fetch this from API if needed)
  const availableRoles = [
    { id: 1, name: "Dealer" },
    { id: 2, name: "Salesman" },
    { id: 4, name: "Customer" },
  ];

  return (
    <DashboardLayout>
      <FullScreenLoader 
        show={creating} 
        message={creating ? "Creating banner..." : ""} 
        variant="primary"
        blurIntensity="md"
      />
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center space-x-3 mb-6">
            <button
              onClick={() => navigate(-1)}
              className="hover:text-gray-800 transition"
            >
              <FaArrowLeft size={20} />
            </button>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">
              Create Banner
            </h1>
          </div>

          <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
            {/* Step 1: Basic Information */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b-2 border-amber-500">
                Basic Information
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Banner Type <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="banner_type"
                    value={formData.banner_type}
                    onChange={handleChange}
                    placeholder="e.g., Home Banner, Promotional Banner"
                    className={`w-full px-4 py-2.5 border-2 rounded-lg focus:outline-none focus:border-amber-500 transition ${
                      errors.banner_type ? "border-red-500" : "border-gray-300"
                    }`}
                    required
                  />
                  {errors.banner_type && (
                    <p className="text-red-500 text-sm mt-1">{errors.banner_type}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Banner Image <span className="text-red-500">*</span>
                  </label>
                  {formData.imagePreview ? (
                    <div className="relative">
                      <img
                        src={formData.imagePreview}
                        alt="Preview"
                        className="w-full h-64 object-cover rounded-lg border-2 border-gray-300"
                      />
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition"
                      >
                        <FiX size={20} />
                      </button>
                    </div>
                  ) : (
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-amber-500 transition"
                    >
                      <FaUpload className="mx-auto text-gray-400 text-4xl mb-2" />
                      <p className="text-gray-600 mb-1">Click to upload image</p>
                      <p className="text-gray-500 text-sm">PNG, JPG, GIF up to 5MB</p>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                  {errors.image && (
                    <p className="text-red-500 text-sm mt-1">{errors.image}</p>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="is_live"
                    id="is_live"
                    checked={formData.is_live}
                    onChange={handleChange}
                    className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                  />
                  <label htmlFor="is_live" className="text-sm font-medium text-gray-700">
                    Make banner live immediately
                  </label>
                </div>
              </div>
            </div>

            {/* Step 2: Target Roles */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b-2 border-amber-500">
                Target Roles (Optional)
              </h2>
              <div className="space-y-2">
                {availableRoles.map((role) => (
                  <label
                    key={role.id}
                    className="flex items-center gap-2 cursor-pointer p-3 rounded-lg hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      checked={formData.role_ids.includes(role.id)}
                      onChange={() => handleRoleChange(role.id)}
                      className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                    />
                    <span className="text-sm font-medium text-gray-700">{role.name}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Step 3: Schedule (Optional) */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b-2 border-amber-500">
                Schedule (Optional)
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FiCalendar className="inline mr-2" />
                    Start Date
                  </label>
                  <input
                    type="datetime-local"
                    name="start_date"
                    value={formData.start_date}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-amber-500 transition"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FiCalendar className="inline mr-2" />
                    End Date
                  </label>
                  <input
                    type="datetime-local"
                    name="end_date"
                    value={formData.end_date}
                    onChange={handleChange}
                    className={`w-full px-4 py-2.5 border-2 rounded-lg focus:outline-none focus:border-amber-500 transition ${
                      errors.end_date ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                  {errors.end_date && (
                    <p className="text-red-500 text-sm mt-1">{errors.end_date}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 sm:gap-4 mt-6 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="flex items-center justify-center w-10 h-10 sm:w-auto bg-white border-2 border-red-500 text-red-600 p-2 sm:py-2.5 sm:px-6 rounded-lg hover:bg-red-50 font-semibold transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <FiX size={20} className="sm:mr-2" />
                <span className="hidden sm:inline">Cancel</span>
              </button>

              <button
                type="submit"
                disabled={creating}
                className="w-full sm:w-auto bg-gradient-to-r from-amber-500 to-amber-600 text-white py-2.5 px-6 rounded-lg hover:from-amber-600 hover:to-amber-700 font-semibold transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {creating ? "Creating..." : "Create Banner"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CreateBanner;

